// Catalog.ID Client — Bitcash Runtime Context
// ============================================================================
// How this client reaches the Bitcash v2 ledger (apps/bitcash/docs/plans/bitcash-v2.md M12, §4.16):
// only through what the node announces in `GET /api/registration/config` —
// `ledgerUrl` (`Bitcash:LedgerUrl`) and `networkId` (refs and view codes are
// bound to it). A node that names no ledger gets no ledger call at all: there
// is no built-in or build-time default any more. There is no Operator receive
// address here: the member's default token is minted unbound
// (apps/docs/plans/second-node-and-bitcash-open-issues.md §9.3 D-c).
//
// ── no routing seam ─────────────────────────────────────────────────────────
// The pilot Worker routed on a code's second group, so codes minted for it
// carried its host label there (docs/cid-governance.md M13). The v2 ledger
// routes on nothing, and this client talks to nothing else, so every code and
// lock key it mints is the v2 layout (R29): `crypto/bitcash.ts` takes no
// routing prefix at all, and a pilot-layout lock key cannot be minted here.

import { fetchRegistrationConfig, getLedgerUrl, type NetworkId } from '../auth/payment-flow';
import { createBitcashWorker, type BitcashWorker } from '../api/bitcash-worker';

/** The ledger as the node announced it. */
export interface LedgerContext {
    ledgerUrl: string;
    networkId: NetworkId;
}

/**
 * The ledger this client may talk to, or null — a node that names no ledger (or
 * no network for it), and a node that cannot be asked. Callers treat null as
 * "skip the money leg", never as an error that blocks a print.
 */
export async function ledgerContext(): Promise<LedgerContext | null> {
    const config = await fetchRegistrationConfig();
    if (!config.ledgerUrl || !config.networkId) return null;
    return { ledgerUrl: config.ledgerUrl, networkId: config.networkId };
}

let cachedWorker: BitcashWorker | null = null;

/**
 * The ledger client. Its base URL is resolved LAZILY on every call, so a client
 * built before `GET /api/registration/config` answered lands on the right
 * origin once it has — and refuses with `no_ledger`, sending nothing, before.
 */
export function bitcashWorker(): BitcashWorker {
    cachedWorker ??= createBitcashWorker({ baseUrl: () => getLedgerUrl() });
    return cachedWorker;
}

/** Test seam: drop the memoised client. */
export function resetBitcashWorker(): void {
    cachedWorker = null;
}
